import { createSelector } from 'reselect'
import path from 'ramda/es/path'
import pick from 'ramda/es/pick'
import get from 'app/common/lib/get'

import config from 'app/config'
import { getIsStudent } from 'app/selectors/user'
import {
  APP_ACCESS,
  APP_POLLS,
  APP_DNEVNIK,
  APP_CAFETERIA,
  EXT_ELJUR,
  EXT_CUSTOM,
} from 'app/common/constants/services'
import {
  TARIFF_FIRST_OF_MONTH,
  TARIFF_MONTHLY,
  TARIFF_EXTERNAL_SYSTEM,
} from 'app/common/constants/tariffs'
import apps from 'app/components/Subs/applications'
import * as orgsTypes from 'app/common/constants/schools/types'
import { selectCurrencySign } from 'app/common/components-next/currency'

import getAppTranslateIdWithPrefix from '../i18n/getAppTranslateIdWithPrefix'

const getPathToCafeSettings = (studentId, isPaid, isSettingsFree) => {
  const pathToCafe = `/apps/${studentId}/cafeteria/settings`
  if (isPaid) return pathToCafe
  if (isSettingsFree) return pathToCafe
  return ''
}

/**
 * TODO: перенести подключение к состоянию в каждую конкретную карточку
 * TODO: отсюда все убрать
 */
export default createSelector(
  [
    (_, { id }) => id,
    getIsStudent,
    (state) => state.apps.data,
    (state) => state.students.data,
    (state) => state.apps.subs,
    (state) => state.settings.fav,
    (state) => state.apps.tariffs,
    (state) => state.apps.tariffBundles,
    (state) => state.cafeteria.sections.free.settings,
    (_, { t }) => t,
    selectCurrencySign,
  ],
  (
    id,
    isStudent,
    data,
    studentsData,
    subs,
    fav,
    tariffs,
    tariffBundles,
    isCafeSettingsFree,
    t,
    currency,
  ) => {
    const currentOrgType = path([id, 'orgType'], studentsData)

    return apps
      .filter((app) => {
        // здесь дублируется с селектором getIsAppSubscribed в selectors/applications
        // Отдельный случай -- приложение "Голосования"
        if (app === APP_POLLS) {
          return !!get(data, 'polls.students', []).includes(parseInt(id, 10))
        }
        // Проходная не отображается при логине ребенка (c 11.12.2020 только для заведений типа школа и садик)
        if (
          app === APP_ACCESS &&
          (currentOrgType === orgsTypes.SCHOOL || currentOrgType === orgsTypes.PRESCHOOL) &&
          isStudent
        ) {
          return false
        }
        // Столовая не поддерживается в школе ученика
        // if (app === APP_CAFETERIA && !get(studentsData, [id, 'hasCafeteria'], false)) {
        //   return false;
        // }
        // изменения по столовой от 20.12.19
        // если поддерживается, то теперь всегда будет во включенных, независимо от подписки
        if (app === APP_CAFETERIA && get(studentsData, [id, 'hasCafeteria'], false)) {
          return true
        }
        // Дневник не поддерживается в школе (любой дневник)
        if (app === APP_DNEVNIK && !get(studentsData, [id, 'hasDnevnik'], false)) {
          return false
        }
        // Случай внешнего дневника и нет подписки
        if (app === APP_DNEVNIK && get(studentsData, [id, 'dnevnikExternalService'], false)) {
          return true // !!
        }
        // Случай внешней проходной и нет подписки
        if (app === APP_ACCESS && get(studentsData, [id, 'passagesExternalService'], false)) {
          return true // !!
        }
        // Случай внешней столовой и нет подписки
        if (app === APP_CAFETERIA && get(studentsData, [id, 'cafeteriaExternalService'], false)) {
          return true // !!
        }
        // Остальные случаи (есть подписка)
        const subId = get(data, [app, id], null)
        return !!subs[subId]
      })
      .map((app) => {
        // Голосования
        if (app === APP_POLLS) {
          return {
            key: app,
            service: app,
            activeCount: get(data, [app, 'active'], 0),
            newCount: get(data, [app, 'new'], 0),
            isChosen: fav && fav[app],
            appLink: `/app/${get(studentsData, [id, 'schoolId'], 0)}/fund`,
            fullName: t(getAppTranslateIdWithPrefix(currentOrgType, app)),
            name: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'short' }),
            type: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'type' }),
          }
        }
        // Внешний дневник (eljur)
        if (
          app === APP_DNEVNIK &&
          get(studentsData, [id, 'dnevnikExternalService'], false) &&
          get(studentsData, [id, 'dnevnikExternalServiceType'], '') === 'eljur'
        ) {
          return {
            key: EXT_ELJUR,
            service: APP_DNEVNIK,
            serviceType: 'eljur',
            externalInfo: t(
              'app_dnevnik_description_eljur',
              'Услуга «Электронный дневник» в школе, где обучается ваш ребенок, предоставляется компанией «ЭлЖур»',
            ),
            isChosen: fav && fav[app],
            appLink: `/apps/${id}/${app}`,
            fullName: t(getAppTranslateIdWithPrefix(currentOrgType, app)),
            name: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'short' }),
            type: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'type' }),
          }
        }
        // Внешний дневник (custom)
        if (
          app === APP_DNEVNIK &&
          get(studentsData, [id, 'dnevnikExternalService'], false) &&
          get(studentsData, [id, 'dnevnikExternalServiceType'], '') === 'custom'
        ) {
          return {
            key: EXT_CUSTOM,
            service: APP_DNEVNIK,
            serviceType: 'custom',
            externalInfo: t('app_dnevnik_description_custom', {
              schoolName: get(studentsData, [id, 'school'], ''),
              serviceName: get(studentsData, [id, 'dnevnikExternalServiceName'], ''),
            }),
            externalLink: get(studentsData, [id, 'dnevnikExternalServiceLink'], ''),
            externalIcon: get(studentsData, [id, 'dnevnikExternalServiceIcon'], ''),
            isChosen: fav && fav[app],
            appLink: `/apps/${id}/${app}`,
            fullName: t(getAppTranslateIdWithPrefix(currentOrgType, app)),
            name: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'short' }),
            type: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'type' }),
          }
        }

        const subId = get(data, [app, id], null)

        // Внешняя проходная или столовая
        // с 20.12.19 ...или столовая без подписки на платный тариф
        if (
          !subId &&
          ((app === APP_ACCESS && get(studentsData, [id, 'passagesExternalService'], false)) ||
            (app === APP_CAFETERIA && get(studentsData, [id, 'cafeteriaExternalService'], false)) ||
            (app === APP_CAFETERIA && !subId))
        ) {
          return {
            key: app,
            service: app,
            studentId: parseInt(id, 10),
            freeServices: subs[subId]?.free_services || false,
            tariffName: t('tariff:free_tariff_title1', 'Бесплатный'),
            freeTariff: true,
            tariffCost: t('tariff:free_tariff_title2', { currency }), // '0 ₽/мес.',
            isChosen: fav && fav[app],
            appLink: `/apps/${id}/${app}`,
            tariff: {
              lk_provided: true,
              mobile_provided: true,
              sms_provided: true,
              email_provided: true,
              lk_enabled: true,
              mobile_enabled: true,
              sms_enabled: false,
              email_enabled: false,
            },
            settings:
              app === APP_CAFETERIA ? getPathToCafeSettings(id, !!subId, isCafeSettingsFree) : '',
            fullName: t(getAppTranslateIdWithPrefix(currentOrgType, app)),
            name: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'short' }),
            type: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'type' }),
          }
        }
        // Обычное приложение
        const sub = get(subs, [subId], { tariff: {}, modified: {} })
        const someIds = pick(['tariff_bundle_id', 'tariff_id'], sub)
        const tariffOrig = someIds.tariff_id && tariffs[someIds.tariff_id]
        const bundleOrig = someIds.tariff_bundle_id && {
          // первый из набора в пакете
          ...tariffs[path([someIds.tariff_bundle_id, 'tariff_ids', 0], tariffBundles)],
          // данные пакета
          ...tariffBundles[someIds.tariff_bundle_id],
        }

        const tariff = { ...(bundleOrig || tariffOrig), ...sub.modified }

        return ({
          key: subId + app,
          subId,
          service: app,
          studentId: parseInt(id, 10),
          appLink: `/apps/${id}/${app}`,
          freeServices: subs[subId]?.free_services || false,
          tariffName: tariff.title,
          isPaused: sub.is_paused,
          isOutOfDate: !!sub.old && tariff.kind !== TARIFF_EXTERNAL_SYSTEM,
          tariffCost: tariff.kind === TARIFF_EXTERNAL_SYSTEM ? '' : tariff.cost,
          tariff,
          showIndicator: tariff.kind === TARIFF_FIRST_OF_MONTH || tariff.kind === TARIFF_MONTHLY,
          isChosen: fav && fav[app],
          settings:
            app === APP_CAFETERIA ? getPathToCafeSettings(id, !!subId, isCafeSettingsFree) : '',
          fullName: t(getAppTranslateIdWithPrefix(currentOrgType, app)),
          name: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'short' }),
          type: t(getAppTranslateIdWithPrefix(currentOrgType, app), { context: 'type' }),
        }
        )
      }
      )
  })
